import type { GraphQLError } from 'graphql'
import { FetchError } from 'ofetch'

// GraphQL Response Types
// -----------------------------------------------------------------------------
/**
 * Generic GraphQL Response Type
 *
 * @template T - The type of the data returned in the response
 */
export type GQLResponse<T> = GQLSuccessResponse<T> | GQLErrorResponse

export type GQLSuccessResponse<T> = {
  data: T
}

export type GQLErrorResponse = {
  errors: GraphQLError[]
}

// GraphGL Fetch Utility
// -----------------------------------------------------------------------------
export type GQLResult<T, E = GQLResultError> = Result<T, E>

export type GQLResultError = GQLResultDefaultError | GQLResultAbortError

export interface GQLResultDefaultError<A = Record<string, unknown>> {
  statusCode: number
  message: string
  originalErrors?: GraphQLError[]
  additionalInfo?: A
}
export interface GQLResultAbortError {
  statusCode: 0
  message: string
  wasAborted: true
}

/**
 * Parses GraphQL errors from the response and constructs a GQLResultError.
 *
 * @param response - The GraphQL error response
 * @returns A GQLResultError containing the status code and concatenated error messages
 */
export function parseGQLErrors(response: GQLErrorResponse): GQLResultDefaultError {
  return {
    statusCode: 500,
    message: response.errors
      .map((e) => {
        return e.message
      })
      .join('\n'),
    originalErrors: response.errors,
  }
}

/**
 * Parses fetch errors and constructs a GQLResultError.
 *
 * @param error - The error thrown during the fetch operation
 * @returns A GQLResultError containing the status code and error message
 */
export function parseFetchError(error: unknown): GQLResultError {
  if (error instanceof FetchError) {
    return {
      statusCode: error.statusCode ?? 500,
      message: error.message,
    }
  }

  return {
    statusCode: 500,
    message: 'An unknown error occurred during fetch.',
  }
}

export function isAbortErr<T>(result: GQLResult<T>): result is Err<GQLResultAbortError> {
  if (isErr(result) && 'wasAborted' in result.error) {
    return true
  }
  return false
}

export function logGQLError(err: Err<GQLResultError>): void {
  const messageParts: string[] = [`GraphQL Error ${err.error.statusCode}:\n${err.error.message}\n`]

  if ('originalErrors' in err.error && err.error.originalErrors) {
    for (const originalError of err.error.originalErrors) {
      if (originalError.extensions?.debugMessage) {
        messageParts.push(`- Debug Message: ${originalError.extensions.debugMessage}\n`)
      }
    }
  }

  messageParts.push(`Stack Trace: ${err.stackTrace}`)

  console.error(messageParts.join(''))
}

export function hasValidationError(errorContainer: { errors?: Array<string | null> | null }): boolean {
  return isNonNullish(errorContainer.errors) && errorContainer.errors.length > 0
}
